import { type FC, type ReactNode } from 'react';
import { graphql, useFragment } from 'react-relay';
import { FormattedMessage, useIntl } from 'dibs-react-intl';

import dibsCss from 'dibs-css';
import classnames from 'classnames';
import { getIsNewSeller } from '../../utils/sellerHelpers.ts';
import styles from './PdpSharedDealerInfo.scss';
import { type PdpSharedDealerInfo_viewer$key } from './__generated__/PdpSharedDealerInfo_viewer.graphql.ts';
import { type PdpSharedDealerInfo_seller$key } from './__generated__/PdpSharedDealerInfo_seller.graphql.ts';

const viewerFragment = graphql`
    fragment PdpSharedDealerInfo_viewer on Viewer {
        suppressNewSellerSignals: featureFlag(feature: "suppressNewSellerSignals")
    }
`;
const sellerFragment = graphql`
    fragment PdpSharedDealerInfo_seller on Seller {
        agreementStart
        sellerOrderQuantity
        analytics {
            metrics {
                responseTime {
                    value
                }
            }
        }
        tradeAssociations {
            serviceId
            displayName
        }
        liveStorefrontProfile {
            establishmentYear
        }
        monthsSinceStartDate: timeSinceStartDate(unit: MONTH)
    }
`;

const MINIMUM_ORDER_QUANTITY = 5;

const formatResponseTime = (responseTime: number | null | undefined): ReactNode => {
    if (responseTime === null || responseTime === undefined) {
        return null;
    }

    if (responseTime > 168) {
        return (
            <FormattedMessage id="PdpSharedDealerInfo.response_time_week" defaultMessage="A week" />
        );
    }

    if (responseTime > 48) {
        return (
            <FormattedMessage
                id="PdpSharedDealerInfo.response_time_several_days"
                defaultMessage="Several days"
            />
        );
    }

    if (responseTime > 23) {
        return (
            <FormattedMessage
                id="PdpSharedDealerInfo.response_time_few_days"
                defaultMessage="1 to 2 days"
            />
        );
    }

    return (
        <FormattedMessage
            id="PdpSharedDealerInfo.response_time_value"
            defaultMessage="{responseTime, select,
              0 {<1 hour}
              1 {1 hour}
              other {{responseTime} hours}
            }"
            values={{ responseTime }}
        />
    );
};

type Props = {
    viewer: PdpSharedDealerInfo_viewer$key | null;
    seller: PdpSharedDealerInfo_seller$key;
};

export const PdpSharedDealerInfo: FC<Props> = ({ viewer: viewerRef, seller: sellerRef }) => {
    const viewer = useFragment(viewerFragment, viewerRef);
    const seller = useFragment(sellerFragment, sellerRef);
    const { formatNumber } = useIntl();
    const sellerOrderQuantity = seller?.sellerOrderQuantity;
    const establishmentYear = seller?.liveStorefrontProfile?.establishmentYear;
    const tradeAssociations = seller?.tradeAssociations || [];
    const dealerSince = seller?.agreementStart
        ? new Date(seller.agreementStart).getUTCFullYear()
        : null;
    const isNewSeller = getIsNewSeller(seller?.monthsSinceStartDate);
    const showDealerSince = dealerSince && (!isNewSeller || !viewer?.suppressNewSellerSignals);
    const responseTime = formatResponseTime(seller?.analytics?.metrics?.responseTime?.value);

    return (
        <>
            {establishmentYear && (
                <div className={styles.info} data-tn="dealer-establishmentYear">
                    <FormattedMessage
                        id="PdpSharedDealerInfo.establishmentYear"
                        defaultMessage="Established in {establishmentYear}"
                        values={{ establishmentYear }}
                    />
                </div>
            )}
            {showDealerSince && (
                <div className={styles.info} data-tn="dealer-since">
                    <FormattedMessage
                        id="PdpSharedDealerInfo.since"
                        defaultMessage="1stDibs seller since {dealerSince}"
                        values={{ dealerSince }}
                    />
                </div>
            )}
            {sellerOrderQuantity && sellerOrderQuantity >= MINIMUM_ORDER_QUANTITY && (
                <div className={styles.info} data-tn="dealer-orderQuantity">
                    <FormattedMessage
                        id="PdpSharedDealerInfo.sellerOrderQuantity"
                        defaultMessage="{sellerOrderQuantity} sales on 1stDibs"
                        values={{ sellerOrderQuantity: formatNumber(sellerOrderQuantity) }}
                    />
                </div>
            )}
            {responseTime && (
                <div className={styles.info} data-tn="dealer-response-time">
                    <FormattedMessage
                        id="PdpSharedDealerInfo.response_time"
                        defaultMessage="Typical response time: {responseTime}"
                        values={{ responseTime }}
                    />
                </div>
            )}
            {!!tradeAssociations.length && (
                <div
                    className={classnames(
                        styles.associations,
                        dibsCss.mtSmall,
                        dibsCss.mbMedium,
                        dibsCss.ptXsmall
                    )}
                    data-tn="dealer-associations"
                >
                    <FormattedMessage
                        id="PdpSharedDealerInfo.associations"
                        defaultMessage="Associations"
                    />

                    <div className={classnames(dibsCss.flex, dibsCss.flexCol)}>
                        {tradeAssociations.map(association => (
                            <span key={association?.serviceId} className={styles.info}>
                                {association?.displayName}
                            </span>
                        ))}
                    </div>
                </div>
            )}
        </>
    );
};
